import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
  App,
  Button,
  DatePicker,
  Dropdown,
  message,
  Modal,
  Select,
  Spin,
  Tooltip,
} from 'antd';
import {
  faAnglesDown,
  faAnglesLeft,
  faAnglesRight,
  faAnglesUp,
  faBoxOpen,
  faCalendar,
  faCarSide,
  faChevronDown,
  faChevronRight,
  faClock,
  faExclamationTriangle,
  faFilter,
  faPen,
  faPhone,
  faPrint,
  faRefresh,
  faTruckRampBox,
  faUnlink,
  faUser,
} from '@/ui';
import {
  CustomColumns,
  CustomTable,
  Icon,
  JobChainDetail,
} from '@/ui/components';
import { defaultTheme } from '@/ui/theme';
import { useAppCustomers } from '@/hooks';
import {
  useAssignLoadToLane,
  useLoadLanes,
  useUnassignLoadFromLane,
  useUpdateLoadLaneMovement,
} from '@/api/LoadLanes/hooks';
import { useJobStatus } from '@/api/Jobs';
import { LoadLaneMovementModel } from '@/types';
import {
  FORMAT_DATE_WITH_TIME_DAYJS,
  LocalStorageKeys,
} from '@/utils/constants';
import {
  dateConverter,
  getLocalStorageValue,
  setLocalStorageValue,
  timeConverter,
} from '@/utils/helpers';
import {
  LOAD_LANE_STATUS_CONFIG,
  LoadLaneStatus,
  STATUS_FILTER_TABS,
} from '../constants';
import { UpcomingDayPicker } from './UpcomingDayPicker';
import {
  AssignLoadButton,
  AssignToLaneButton,
  CarrierBadge,
  CloseSidebarButton,
  DriverCell,
  EmptyState,
  ExpandedTitle,
  ExpandedVehicles,
  FooterLaneChip,
  FooterLaneGrid,
  LaneCell,
  LaneChip,
  LaneGrid,
  LaneLoadBadge,
  LaneSelectButton,
  LaneSelectCancel,
  LaneSelectGrid,
  MainPanel,
  OverdueText,
  PageDate,
  PageFilters,
  PageHeader,
  PageLayout,
  PageShell,
  PageTitle,
  PendingBadge,
  RefreshButton,
  SidebarAssignBanner,
  SidebarAssignBannerTitle,
  SidebarBottom,
  SidebarCollapsedLabel,
  SidebarCollapsedStrip,
  SidebarCollapseToggle,
  SidebarHeader,
  SidebarHeaderActions,
  SidebarMiddle,
  SidebarPanel,
  SidebarSectionTitle,
  SidebarTitle,
  SidebarTop,
  SelectVacantLaneTitle,
  StatusPill,
  StatusTab,
  StatusTabs,
  SummaryBadge,
  SummaryBadges,
  TableCard,
  TablePanel,
  TableFooter,
  TableFooterBar,
  TableFooterExpanded,
  TableFooterStats,
  TableScrollArea,
  Toolbar,
  UpcomingEtaButton,
  UpcomingLoadCard,
  UpcomingLoadHeader,
  UpcomingLoadList,
  UpcomingLoadMeta,
  UpcomingLoadMetaRow,
  UpcomingLoadRef,
  VehicleDot,
  VehicleGrid,
  VehicleDescription,
  VehicleDescriptionCell,
  VehiclePlate,
  VehiclePlateCell,
  VehicleProgress,
  VehicleRow,
  VehicleSquare,
  VehicleSquares,
  VehicleStatus,
  VehicleStatusCell,
  VehicleCount,
  PageTitleText,
} from '../LoadLaneStatus.styled';
import {
  buildLaneTableRows,
  formatDueAt,
  formatLaneNumber,
  formatOverdueDuration,
  getAssignableLaneNumbers,
  getFooterLaneChips,
  getFooterStats,
  getLoadLaneStatus,
  getProviderInitials,
  getStatusCounts,
  getUpcomingLoads,
  getVacantLaneNumbers,
  getVehicleDescription,
  LaneDisplayRow,
  parseEtaDateLocal,
  UpcomingDayFilter,
} from '../utils';

dayjs.extend(advancedFormat);

type StatusFilter = LoadLaneStatus | 'all';
type DateTimeEditField = 'collectionDate' | 'etaDate';
type DateTimeModalState = {
  field: DateTimeEditField;
  movementId: number;
  movementRef: string;
  value?: string;
};

const FILTER_OPERATORS = new Set(['Contains', 'Not', 'Equal', '<=', '>=']);

const isFilterOperator = (value: unknown) =>
  FILTER_OPERATORS.has(String(value ?? ''));

const textIncludes = (source: string, value: unknown) => {
  if (isFilterOperator(value)) return false;
  const query = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!query) return false;
  return source.toLowerCase().includes(query);
};

const DATE_TIME_MODAL_COPY: Record<
  DateTimeEditField,
  { title: string; success: string; error: string }
> = {
  collectionDate: {
    title: 'Change Collection DateTime',
    success: 'Collection date updated.',
    error: 'Failed to update collection date.',
  },
  etaDate: {
    title: 'Change ETA DateTime',
    success: 'ETA date updated.',
    error: 'Failed to update ETA date.',
  },
};

const LoadLaneExpandedRow: React.FC<{ lane: LaneDisplayRow }> = ({ lane }) => {
  return (
    <ExpandedVehicles key={lane.locationLane}>
      <ExpandedTitle>
        VEHICLES — {lane.currentVehicleCount} OF
        {lane.movementTotalStockCount} PULLED{' '}
        <span className="mx-5">{timeConverter(lane.createdAt)}</span>{' '}
        {timeConverter(lane.lastUpdatedAt)}
      </ExpandedTitle>
      <VehicleGrid>
        {lane.vehicles.map((vehicle) => (
          <VehicleRow key={vehicle.stockID}>
            <VehicleDot $pulled={vehicle.isOnSite} />
            <Tooltip
              title={
                <div className="fs-10">
                  <div>
                    {vehicle.vin} | {vehicle.regNumber}
                  </div>
                  <div>
                    {vehicle.productDesc} |{' '}
                    {vehicle.manufactureDate
                      ? dateConverter(vehicle.manufactureDate, 'YYYY')
                      : ''}{' '}
                    | {vehicle.colourDesc}
                  </div>
                  <div>
                    Location:{' '}
                    {vehicle.locationFullDesc &&
                    vehicle.locationFullDesc.length > 0
                      ? vehicle.locationFullDesc
                      : '-'}
                  </div>
                </div>
              }
            >
              <VehiclePlateCell>
                <VehiclePlate
                  $newAdded={vehicle.isAdded}
                  $removed={vehicle.isRemoved}
                >
                  {vehicle.regNumber || vehicle.vin}
                </VehiclePlate>
              </VehiclePlateCell>
            </Tooltip>
            <VehicleDescriptionCell>
              <VehicleDescription
                $newAdded={vehicle.isAdded}
                $removed={vehicle.isRemoved}
              >
                {getVehicleDescription(vehicle)}
              </VehicleDescription>
            </VehicleDescriptionCell>
            <Tooltip
              title={
                vehicle.isOnSite ? (
                  <div className="fs-10">
                    <div>Pulled at {timeConverter(vehicle.pulledDate)}</div>
                    <div className="fw-light">by {vehicle.pulledBy}</div>
                  </div>
                ) : undefined
              }
            >
              <VehicleStatusCell>
                <VehicleStatus $pulled={vehicle.isOnSite}>
                  {vehicle.isRemoved
                    ? null
                    : vehicle.isOnSite
                      ? 'Pulled'
                      : 'Pending'}
                </VehicleStatus>
              </VehicleStatusCell>
            </Tooltip>
          </VehicleRow>
        ))}
      </VehicleGrid>
    </ExpandedVehicles>
  );
};

const UpcomingLoadCardItem: React.FC<{
  load: LoadLaneMovementModel;
  assignableLanes: string[];
  assigningLane?: string;
  isAssigning: boolean;
  canOperate: boolean;
  onAssignClick: (lane: string) => Promise<void>;
  onEditEta: (load: LoadLaneMovementModel) => void;
}> = ({
  load,
  assignableLanes,
  assigningLane,
  isAssigning,
  canOperate,
  onAssignClick,
  onEditEta,
}) => {
  const [isSelectingLane, setIsSelectingLane] = useState(false);

  useEffect(() => {
    setIsSelectingLane(false);
  }, [assigningLane]);

  return (
    <Spin spinning={isAssigning}>
      <UpcomingLoadCard>
        <UpcomingLoadHeader>
          <UpcomingLoadRef>{load.movementRef}</UpcomingLoadRef>
          <CarrierBadge title={load.providerName}>
            {getProviderInitials(load.providerName)}
          </CarrierBadge>
        </UpcomingLoadHeader>
        <UpcomingLoadMeta>
          <UpcomingLoadMetaRow>
            <UpcomingEtaButton
              type="button"
              disabled={!canOperate}
              onClick={() => canOperate && onEditEta(load)}
              title={canOperate ? 'Change ETA DateTime' : undefined}
            >
              <Icon icon={faClock} color="#718096" />
              {formatDueAt(load.etaDate)}
              {canOperate ? <Icon icon={faPen} color="#718096" /> : null}
            </UpcomingEtaButton>
          </UpcomingLoadMetaRow>
          |
          <UpcomingLoadMetaRow>
            <Icon icon={faCarSide} color="#718096" />
            {load.movementTotalStockCount || load.vehicles.length} vehicles
          </UpcomingLoadMetaRow>
        </UpcomingLoadMeta>
        {isSelectingLane && !assigningLane ? (
          <>
            <SelectVacantLaneTitle>Select a lane:</SelectVacantLaneTitle>
            {assignableLanes.length > 0 ? (
              <LaneSelectGrid>
                {assignableLanes.map((lane) => (
                  <LaneSelectButton
                    key={lane}
                    type="button"
                    disabled={!canOperate}
                    onClick={async () => {
                      await onAssignClick(lane);
                      setIsSelectingLane(false);
                    }}
                  >
                    Lane {formatLaneNumber(lane)}
                  </LaneSelectButton>
                ))}
              </LaneSelectGrid>
            ) : (
              <EmptyState>No lanes available</EmptyState>
            )}
            <LaneSelectCancel
              type="button"
              onClick={() => setIsSelectingLane(false)}
            >
              Cancel
            </LaneSelectCancel>
          </>
        ) : canOperate ? (
          <AssignToLaneButton
            type="button"
            onClick={() =>
              assigningLane
                ? onAssignClick(assigningLane)
                : setIsSelectingLane(true)
            }
            $variant={assigningLane ? 'primary' : 'secondary'}
          >
            {assigningLane
              ? `Assign to Lane ${formatLaneNumber(assigningLane)}`
              : 'Assign to Lane'}
          </AssignToLaneButton>
        ) : null}
      </UpcomingLoadCard>
    </Spin>
  );
};

export const LoadLaneStatusPage: React.FC = () => {
  const { userPermissions, hasLoadLaneOperateAccess } = useAppCustomers();
  const { modal } = App.useApp();
  const canOperate = hasLoadLaneOperateAccess;

  const availableCustomers = useMemo(
    () => userPermissions.customers ?? [],
    [userPermissions.customers],
  );

  const availableLocations = useMemo(
    () => userPermissions.locations ?? [],
    [userPermissions.locations],
  );

  const [selectedCustomerId, setSelectedCustomerId] = useState<number>();
  const [selectedSiteId, setSelectedSiteId] = useState<number>();

  useEffect(() => {
    if (!availableCustomers.length) return;

    setSelectedCustomerId((current) => {
      if (
        current &&
        availableCustomers.some((customer) => customer.customerId === current)
      ) {
        return current;
      }

      const cachedCustomerId = Number(
        getLocalStorageValue(LocalStorageKeys.LoadLaneCustomerId, ''),
      );
      if (
        cachedCustomerId &&
        availableCustomers.some(
          (customer) => customer.customerId === cachedCustomerId,
        )
      ) {
        return cachedCustomerId;
      }

      return availableCustomers[0].customerId;
    });
  }, [availableCustomers]);

  useEffect(() => {
    if (!availableLocations.length) return;

    setSelectedSiteId((current) => {
      if (
        current &&
        availableLocations.some((location) => location.locationId === current)
      ) {
        return current;
      }

      const cachedSiteId = Number(
        getLocalStorageValue(LocalStorageKeys.LoadLaneSiteId, ''),
      );
      if (
        cachedSiteId &&
        availableLocations.some(
          (location) => location.locationId === cachedSiteId,
        )
      ) {
        return cachedSiteId;
      }

      return availableLocations[0].locationId;
    });
  }, [availableLocations]);

  const selectedCustomer = useMemo(
    () =>
      availableCustomers.find(
        (customer) => customer.customerId === selectedCustomerId,
      ),
    [availableCustomers, selectedCustomerId],
  );

  const selectedSite = useMemo(
    () =>
      availableLocations.find(
        (location) => location.locationId === selectedSiteId,
      ),
    [availableLocations, selectedSiteId],
  );

  const { data, isError, isFetching, error, refetch } = useLoadLanes(
    selectedSite?.locationId,
    selectedCustomer?.customerId,
  );
  const { mutateAsync: assignLoadAsync, isPending: isAssigningLoad } =
    useAssignLoadToLane(selectedSite?.locationId, selectedCustomer?.customerId);
  const { mutateAsync: unassignLoadAsync, isPending: isUnassigningLoad } =
    useUnassignLoadFromLane(
      selectedSite?.locationId,
      selectedCustomer?.customerId,
    );
  const { mutateAsync: updateMovementAsync, isPending: isUpdatingMovement } =
    useUpdateLoadLaneMovement(
      selectedSite?.locationId,
      selectedCustomer?.customerId,
    );
  // const {
  //   mutateAsync: createLoadPullRequestJobAsync,
  //   isPending: isCreatingLoadPullRequestJob,
  // } = useCreateLoadPullRequestJob({
  //   siteId: selectedSite?.locationId,
  //   customerId: selectedCustomer?.customerId,
  // });

  const [upcomingDayFilter, setUpcomingDayFilter] =
    useState<UpcomingDayFilter>('today');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [assigningLane, setAssigningLane] = useState<string>();
  const [assigningMovementId, setAssigningMovementId] = useState<number>();
  const [isFooterLaneGridExpanded, setIsFooterLaneGridExpanded] =
    useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [dateTimeModal, setDateTimeModal] = useState<DateTimeModalState>();
  const [dateTimeValue, setDateTimeValue] = useState<Dayjs | null>(null);
  const [selectedJobChainId, setSelectedJobChainId] = useState<number | null>(
    null,
  );
  const { data: jobStatus } = useJobStatus();
  const detailJobStatus = useMemo(
    () =>
      (jobStatus?.filter((o) => o.name !== 'CancelledBySystem') || []).map(
        (o) => ({
          jobStatusId: o.jobStatusId,
          name: o.name,
          description:
            o.name === 'CancelledByUser'
              ? 'Cancelled'
              : o.description || o.name,
        }),
      ),
    [jobStatus],
  );
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState(320);

  const startAssignToLane = useCallback(
    (lane: string) => {
      if (!canOperate) return;
      setIsSidebarCollapsed(false);
      setAssigningLane(lane);
    },
    [canOperate],
  );

  const handleRemoveFromLane = useCallback(
    (lane: LaneDisplayRow) => {
      if (!canOperate || !selectedSite || !selectedCustomer || !lane.movementID)
        return;

      const totalCount = lane.movementTotalStockCount;
      const currentVehicleCount = lane.currentVehicleCount;
      const movementRef = lane.movementRef || `Load ${lane.movementID}`;
      const laneLabel = formatLaneNumber(lane.locationLane);

      modal.confirm({
        title: 'Remove from lane',
        content:
          (currentVehicleCount > 0
            ? `${currentVehicleCount}/${totalCount} vehicles have already been pulled. `
            : '') +
          `Are you sure you want to remove ${movementRef} from Lane ${laneLabel}?`,
        okText: 'Remove',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            const response = await unassignLoadAsync({
              siteId: selectedSite.locationId,
              customerId: selectedCustomer.customerId,
              movementId: lane.movementID!,
            });

            if (!response.success) {
              message.error(
                response.message?.Message ?? 'Failed to remove load from lane.',
              );
              return;
            }

            message.success(`${movementRef} removed from Lane ${laneLabel}.`);
            if (assigningLane === lane.locationLane) {
              setAssigningLane(undefined);
            }
          } catch {
            message.error('Failed to remove load from lane.');
          }
        },
      });
    },
    [
      assigningLane,
      canOperate,
      modal,
      selectedCustomer,
      selectedSite,
      unassignLoadAsync,
    ],
  );

  // const handleCreateLoadPullRequestJob = useCallback(
  //   (lane: LaneDisplayRow) => {
  //     if (!canOperate || !lane.movementRef) return;

  //     const movementRef = lane.movementRef;

  //     modal.confirm({
  //       title: 'Create Load Pull Request Job',
  //       content: `Create a Load Pull Request job for ${movementRef}?`,
  //       okText: 'Create',
  //       cancelText: 'Cancel',
  //       onOk: async () => {
  //         try {
  //           const response = await createLoadPullRequestJobAsync({
  //             movementRef,
  //           });

  //           if (!response.success) {
  //             message.error(
  //               response.message?.Message ??
  //                 `Failed to create job for ${movementRef}.`,
  //             );
  //             return;
  //           }

  //           message.success(
  //             `Load Pull Request job created for ${movementRef}.`,
  //           );
  //         } catch {
  //           message.error(`Failed to create job for ${movementRef}.`);
  //         }
  //       },
  //     });
  //   },
  //   [canOperate, createLoadPullRequestJobAsync, modal],
  // );

  const openDateTimeModal = useCallback(
    (state: DateTimeModalState) => {
      if (!canOperate) return;
      setDateTimeModal(state);
      setDateTimeValue(parseEtaDateLocal(state.value));
    },
    [canOperate],
  );

  const handleUpdateMovementStatus = useCallback(
    (
      lane: LaneDisplayRow,
      movementStatusCode: 'M300' | 'M400',
      title: string,
      successMessage: string,
    ) => {
      if (!canOperate || !selectedSite || !selectedCustomer || !lane.movementID)
        return;

      const movementRef = lane.movementRef || `Load ${lane.movementID}`;

      modal.confirm({
        title,
        content: `Update ${movementRef} to ${title}?`,
        okText: 'Update',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            const response = await updateMovementAsync({
              siteId: selectedSite.locationId,
              customerId: selectedCustomer.customerId,
              movementId: lane.movementID!,
              movementStatusCode,
            });

            if (!response.success) {
              message.error(
                response.message?.Message ?? `Failed to update ${movementRef}.`,
              );
              return;
            }

            message.success(`${movementRef}: ${successMessage}`);
          } catch {
            message.error(`Failed to update ${movementRef}.`);
          }
        },
      });
    },
    [canOperate, modal, selectedCustomer, selectedSite, updateMovementAsync],
  );

  useEffect(() => {
    const scrollArea = tableScrollRef.current;
    if (!scrollArea) return undefined;

    const updateScrollHeight = () => {
      setTableScrollY(scrollArea.clientHeight);
    };

    updateScrollHeight();

    const observer = new ResizeObserver(updateScrollHeight);
    observer.observe(scrollArea);

    return () => observer.disconnect();
  }, []);

  const lanesData = useMemo(
    () => data || { locationLanes: [], movements: [] },
    [data],
  );

  const dayFilteredLanes = useMemo(
    () => buildLaneTableRows(lanesData),
    [lanesData],
  );

  const upcomingLoads = useMemo(
    () => getUpcomingLoads(lanesData, upcomingDayFilter),
    [lanesData, upcomingDayFilter],
  );

  const vacantLaneNumbers = useMemo(
    () => getVacantLaneNumbers(lanesData),
    [lanesData],
  );

  const assignableLaneNumbers = useMemo(
    () => getAssignableLaneNumbers(lanesData),
    [lanesData],
  );

  const assignableUpcomingLoads = useMemo(() => {
    if (!assigningLane) return upcomingLoads;
    if (!lanesData.locationLanes.includes(assigningLane)) return null;
    return upcomingLoads;
  }, [assigningLane, lanesData.locationLanes, upcomingLoads]);

  const statusCounts = useMemo(
    () => getStatusCounts(dayFilteredLanes),
    [dayFilteredLanes],
  );

  const filteredLanes = useMemo(() => {
    if (statusFilter === 'all') return dayFilteredLanes;
    return dayFilteredLanes.filter(
      (lane) => getLoadLaneStatus(lane) === statusFilter,
    );
  }, [dayFilteredLanes, statusFilter]);

  const footerStats = useMemo(
    () => getFooterStats(filteredLanes),
    [filteredLanes],
  );

  const footerLaneChips = useMemo(
    () => getFooterLaneChips(dayFilteredLanes),
    [dayFilteredLanes],
  );

  const loadLanesErrorMessage =
    error instanceof Error ? error.message : 'Failed to load lanes';

  const columns: CustomColumns<LaneDisplayRow> = useMemo(
    () => [
      {
        key: 'lane',
        title: 'LANE',
        dataIndex: 'locationLane',
        width: 80,
        filter: true,
        onFilter: (value, record) =>
          textIncludes(formatLaneNumber(record.locationLane), value) ||
          textIncludes(record.locationLane, value),
        render: (_value, record) => (
          <LaneCell>
            {formatLaneNumber(record.locationLane)}
            {record.laneLoadCount > 1 ? (
              <LaneLoadBadge>{record.laneLoadIndex}</LaneLoadBadge>
            ) : null}
          </LaneCell>
        ),
      },
      {
        key: 'load',
        title: 'LOAD #',
        dataIndex: 'movementRef',
        width: 140,
        filter: true,
        onFilter: (value, record) =>
          textIncludes(record.movementRef || '', value),
        render: (_value, record) => {
          if (!record.movementID) {
            return canOperate ? (
              <AssignLoadButton
                type="button"
                onClick={() => startAssignToLane(record.locationLane)}
              >
                + Assign Load
              </AssignLoadButton>
            ) : (
              '—'
            );
          }
          return record.movementRef || '—';
        },
      },
      {
        key: 'carrier',
        title: 'CARRIER',
        dataIndex: 'providerName',
        width: 100,
        filter: true,
        onFilter: (value, record) =>
          textIncludes(record.providerName || '', value) ||
          textIncludes(getProviderInitials(record.providerName), value),
        render: (value?: string) => (
          <CarrierBadge title={value}>
            {getProviderInitials(value)}
          </CarrierBadge>
        ),
      },
      {
        key: 'status',
        title: 'STATUS',
        dataIndex: 'movementRef',
        width: 120,
        filter: true,
        renderType: 'select',
        filterOptions: (
          Object.keys(LOAD_LANE_STATUS_CONFIG) as LoadLaneStatus[]
        ).map((status) => ({
          text: LOAD_LANE_STATUS_CONFIG[status].label,
          value: status,
        })),
        onFilter: (value, record) =>
          !isFilterOperator(value) && getLoadLaneStatus(record) === value,
        render: (_value, record) => {
          const status = getLoadLaneStatus(record);
          const config = LOAD_LANE_STATUS_CONFIG[status];
          return (
            <StatusPill $color={config.color} $bg={config.bg} $dot={config.dot}>
              {config.label}
            </StatusPill>
          );
        },
      },
      {
        key: 'dueAt',
        title: 'DUE AT',
        dataIndex: 'etaDate',
        width: 100,
        filter: true,
        onFilter: (value, record) =>
          textIncludes(formatDueAt(record.etaDate), value),
        render: (value?: string) => formatDueAt(value),
      },
      {
        key: 'vehicles',
        title: 'VEHICLES',
        dataIndex: 'movementTotalStockCount',
        width: 160,
        filter: true,
        onFilter: (value, record) => {
          const total =
            record.movementTotalStockCount || record.vehicles.length;
          return textIncludes(
            `${record.currentVehicleCount}/${total || 0}`,
            value,
          );
        },
        render: (_value, record) => {
          const status = getLoadLaneStatus(record);
          const config = LOAD_LANE_STATUS_CONFIG[status];
          const total =
            record.movementTotalStockCount || record.vehicles.length;
          const pulled = record.currentVehicleCount;
          const squares = Array.from(
            { length: total },
            (_, index) => index < pulled,
          );

          return (
            <VehicleProgress>
              <VehicleSquares>
                {squares.map((filled, index) => (
                  <VehicleSquare
                    key={`${record.key}-${index}`}
                    $filled={filled}
                    $color={filled ? config.progress : undefined}
                  />
                ))}
              </VehicleSquares>
              <VehicleCount>
                {pulled}/{total || 0}
              </VehicleCount>
            </VehicleProgress>
          );
        },
      },
      {
        key: 'overdue',
        title: 'OVERDUE',
        dataIndex: 'etaDate',
        width: 100,
        filter: true,
        onFilter: (value, record) =>
          textIncludes(
            formatOverdueDuration(record.completedAt, record.etaDate),
            value,
          ),
        render: (_value, record) => {
          const overdue = formatOverdueDuration(
            record.completedAt,
            record.etaDate,
          );
          return overdue === '—' ? '—' : <OverdueText>{overdue}</OverdueText>;
        },
      },
      {
        title: 'DRIVER',
        dataIndex: 'locationLane',
        width: 140,
        render: () => (
          <DriverCell>
            <Icon icon={faUser} color="#718096" />—
          </DriverCell>
        ),
      },
      {
        title: 'ACTIONS',
        dataIndex: 'locationLane',
        width: 140,
        render: (_value, record) => {
          if (!record.movementID) return null;

          return (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'print',
                    label: 'Print',
                    icon: <Icon icon={faPrint} />,
                  },
                  {
                    key: 'contactCarrier',
                    label: 'Contact Carrier',
                    icon: <Icon icon={faPhone} />,
                  },
                  {
                    key: 'removeLoad',
                    label: 'Remove from lane',
                    icon: <Icon icon={faUnlink} />,
                    disabled: !canOperate || isUnassigningLoad,
                    onClick: () => handleRemoveFromLane(record),
                  },
                  {
                    key: 'changeCollectionDate',
                    label: 'Change Collection DateTime',
                    icon: <Icon icon={faCalendar} />,
                    disabled: !canOperate || isUpdatingMovement,
                    onClick: () =>
                      openDateTimeModal({
                        field: 'collectionDate',
                        movementId: record.movementID!,
                        movementRef:
                          record.movementRef || `Load ${record.movementID}`,
                        value: record.collectionDate,
                      }),
                  },
                  {
                    key: 'loadingInProgress',
                    label: 'Loading in Progress',
                    icon: <Icon icon={faTruckRampBox} />,
                    disabled: !canOperate || isUpdatingMovement,
                    onClick: () =>
                      handleUpdateMovementStatus(
                        record,
                        'M300',
                        'Loading in Progress',
                        'Movement status set to Loading in Progress.',
                      ),
                  },
                  {
                    key: 'loadLoaded',
                    label: 'Load Loaded',
                    icon: <Icon icon={faBoxOpen} />,
                    disabled: !canOperate || isUpdatingMovement,
                    onClick: () =>
                      handleUpdateMovementStatus(
                        record,
                        'M400',
                        'Load Loaded',
                        'Movement status set to Load Loaded.',
                      ),
                  },
                  // ...(record.jobChainId
                  //   ? [
                  //       {
                  //         key: 'viewJob',
                  //         label: 'View Job',
                  //         icon: <Icon icon={faEye} />,
                  //         disabled: !canOperate,
                  //         onClick: () =>
                  //           setSelectedJobChainId(record.jobChainId!),
                  //       },
                  //     ]
                  //   : [
                  //       {
                  //         key: 'createJob',
                  //         label: 'Create Job',
                  //         icon: <Icon icon={faListCheck} />,
                  //         disabled: !canOperate || isCreatingLoadPullRequestJob,
                  //         onClick: () => handleCreateLoadPullRequestJob(record),
                  //       },
                  //     ]),
                ],
              }}
              trigger={['click']}
            >
              <Button size="small">
                Assign action <Icon icon={faChevronDown} />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [
      canOperate,
      handleRemoveFromLane,
      handleUpdateMovementStatus,
      isUnassigningLoad,
      isUpdatingMovement,
      openDateTimeModal,
      startAssignToLane,
    ],
  );

  const getTabCount = (key: StatusFilter) => {
    if (key === 'all') return dayFilteredLanes.length;
    return statusCounts[key];
  };

  const handleAssignLoad = async (
    locationLane: string,
    movementId: number,
    movementRef: string,
  ) => {
    if (!canOperate || !selectedSite || !selectedCustomer) return;

    setAssigningMovementId(movementId);

    try {
      const response = await assignLoadAsync({
        siteId: selectedSite.locationId,
        customerId: selectedCustomer.customerId,
        movementId: movementId,
        locationLane,
      });

      if (!response.success) {
        message.error(
          response.message?.Message ?? 'Failed to assign load to lane.',
        );
        return;
      }

      message.success(
        `${movementRef} assigned to Lane ${formatLaneNumber(locationLane)}.`,
      );
      setAssigningLane(undefined);
    } catch {
      message.error('Failed to assign load to lane.');
    } finally {
      setAssigningMovementId(undefined);
    }
  };

  const handleSaveDateTime = async () => {
    if (!dateTimeModal || !selectedSite || !selectedCustomer) return;
    if (!dateTimeValue) {
      message.error('Please select a date and time.');
      return;
    }

    const copy = DATE_TIME_MODAL_COPY[dateTimeModal.field];

    try {
      const response = await updateMovementAsync({
        siteId: selectedSite.locationId,
        customerId: selectedCustomer.customerId,
        movementId: dateTimeModal.movementId,
        [dateTimeModal.field]: dateTimeValue.utc().toISOString(),
      });

      if (!response.success) {
        message.error(response.message?.Message ?? copy.error);
        return;
      }

      message.success(`${dateTimeModal.movementRef}: ${copy.success}`);
      setDateTimeModal(undefined);
      setDateTimeValue(null);
    } catch {
      message.error(copy.error);
    }
  };

  const handleRefresh = async () => {
    const result = await refetch();
    if (result.isError) {
      message.error(
        result.error instanceof Error
          ? result.error.message
          : 'Failed to load lanes',
      );
    }
  };

  const handleTenantChange = () => {
    setAssigningLane(undefined);
    setAssigningMovementId(undefined);
    setDateTimeModal(undefined);
    setDateTimeValue(null);
  };

  return (
    <PageShell>
      <PageHeader>
        <PageTitle>
          <PageTitleText>Load Lane Status</PageTitleText>
          <PageDate>{dayjs().format('dddd, D MMMM YYYY')}</PageDate>
        </PageTitle>
        <PageFilters>
          <Select
            value={selectedSiteId}
            options={availableLocations.map((location) => ({
              label: location.locationName,
              value: location.locationId,
            }))}
            placeholder="Select site"
            onChange={(siteId) => {
              setSelectedSiteId(siteId);
              setLocalStorageValue(LocalStorageKeys.LoadLaneSiteId, siteId);
              handleTenantChange();
            }}
            style={{ minWidth: 180 }}
            showSearch
          />
          <Select
            value={selectedCustomerId}
            options={availableCustomers.map((customer) => ({
              label: customer.customerName,
              value: customer.customerId,
            }))}
            placeholder="Select customer"
            onChange={(customerId) => {
              setSelectedCustomerId(customerId);
              setLocalStorageValue(
                LocalStorageKeys.LoadLaneCustomerId,
                customerId,
              );
              handleTenantChange();
            }}
            style={{ minWidth: 180 }}
            showSearch
          />
        </PageFilters>
        <SummaryBadges>
          <SummaryBadge
            $bg={LOAD_LANE_STATUS_CONFIG.overdue.tabBg}
            $color={LOAD_LANE_STATUS_CONFIG.overdue.color}
            $border={LOAD_LANE_STATUS_CONFIG.overdue.border}
          >
            <Icon icon={faExclamationTriangle} />
            {statusCounts.overdue} Overdue
          </SummaryBadge>
          <SummaryBadge
            $bg={LOAD_LANE_STATUS_CONFIG.dueSoon.tabBg}
            $color={LOAD_LANE_STATUS_CONFIG.dueSoon.color}
            $border={LOAD_LANE_STATUS_CONFIG.dueSoon.border}
          >
            <Icon icon={faClock} />
            {statusCounts.dueSoon} Due Soon
          </SummaryBadge>
          <SummaryBadge
            $bg={LOAD_LANE_STATUS_CONFIG.onTime.tabBg}
            $color={LOAD_LANE_STATUS_CONFIG.onTime.color}
            $border={LOAD_LANE_STATUS_CONFIG.onTime.border}
          >
            <Icon icon={faExclamationTriangle} />
            {statusCounts.onTime} OnTime
          </SummaryBadge>
          <SummaryBadge
            $bg={LOAD_LANE_STATUS_CONFIG.vacant.tabBg}
            $color={LOAD_LANE_STATUS_CONFIG.vacant.color}
            $border={LOAD_LANE_STATUS_CONFIG.vacant.border}
          >
            {statusCounts.vacant} Vacant
          </SummaryBadge>
          <RefreshButton
            type="button"
            aria-label="Refresh load lanes"
            onClick={() => void handleRefresh()}
          >
            <Icon icon={faRefresh} />
          </RefreshButton>
        </SummaryBadges>
      </PageHeader>

      <PageLayout>
        <Toolbar>
          <StatusTabs>
            <Icon icon={faFilter} color="#718096" />
            {STATUS_FILTER_TABS.map((tab) => {
              const tabConfig =
                tab.key === 'all'
                  ? undefined
                  : LOAD_LANE_STATUS_CONFIG[tab.key];
              const isActive = statusFilter === tab.key;

              return (
                <StatusTab
                  key={tab.key}
                  type="button"
                  $active={isActive}
                  $border={
                    isActive && tab.key === 'all'
                      ? '#9ba8c7'
                      : tabConfig?.border
                  }
                  $tabBg={
                    isActive && tab.key === 'all' ? '#e8eaf2' : tabConfig?.tabBg
                  }
                  $color={
                    isActive && tab.key === 'all'
                      ? defaultTheme.main
                      : tabConfig?.color
                  }
                  onClick={() => setStatusFilter(tab.key)}
                >
                  {tab.label} {getTabCount(tab.key)}
                </StatusTab>
              );
            })}
          </StatusTabs>
        </Toolbar>

        <MainPanel>
          <TablePanel>
            <TableCard>
              <TableScrollArea ref={tableScrollRef}>
                {isError ? (
                  <EmptyState>{loadLanesErrorMessage}</EmptyState>
                ) : (
                  <CustomTable
                    rowKey="key"
                    columns={columns}
                    dataSource={filteredLanes}
                    loading={isFetching}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 1100, y: tableScrollY }}
                    locale={{
                      emptyText: 'No lanes match the current filter',
                    }}
                    expandable={{
                      expandIcon: ({ expanded, onExpand, record }) =>
                        record.vehicles.length > 0 ? (
                          <button
                            type="button"
                            aria-label={
                              expanded ? 'Collapse lane' : 'Expand lane'
                            }
                            onClick={(event) => onExpand(record, event)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              marginRight: 8,
                            }}
                          >
                            <Icon
                              icon={expanded ? faChevronDown : faChevronRight}
                            />
                          </button>
                        ) : null,
                      rowExpandable: (record) => record.vehicles.length > 0,
                      expandedRowRender: (record) => (
                        <LoadLaneExpandedRow lane={record} />
                      ),
                    }}
                  />
                )}
              </TableScrollArea>
              <TableFooter>
                <TableFooterBar>
                  <TableFooterStats>
                    {footerStats.laneCount} lanes shown •{' '}
                    {footerStats.pulledVehicles} vehicles pulled •{' '}
                    {footerStats.plannedVehicles} vehicles planned
                  </TableFooterStats>

                  <Icon
                    icon={isFooterLaneGridExpanded ? faAnglesDown : faAnglesUp}
                    onClick={() =>
                      setIsFooterLaneGridExpanded((expanded) => !expanded)
                    }
                  />
                </TableFooterBar>
                {isFooterLaneGridExpanded ? (
                  <TableFooterExpanded>
                    <FooterLaneGrid>
                      {footerLaneChips.map((lane) => {
                        const statusConfig =
                          LOAD_LANE_STATUS_CONFIG[lane.status];
                        const highlight = lane.hasRemovedAfterCompleted
                          ? 'removed'
                          : lane.hasAddedAfterCompleted
                            ? 'added'
                            : undefined;

                        return (
                          <Tooltip
                            key={lane.locationLane}
                            title={
                              lane.movementID ? (
                                <div className="fs-10">
                                  <div>
                                    Cars pulled: {lane.currentVehicleCount} /{' '}
                                    {lane.movementTotalStockCount || 0}
                                  </div>
                                  <div>Due at: {formatDueAt(lane.etaDate)}</div>
                                  <div>
                                    Last pulled:{' '}
                                    {formatDueAt(lane.lastPulledAt) ?? '—'}
                                  </div>
                                </div>
                              ) : (
                                <div className="fs-10">Vacant</div>
                              )
                            }
                          >
                            <FooterLaneChip
                              $bg={statusConfig.bg}
                              $color={statusConfig.color}
                              $border={statusConfig.border}
                              $highlight={highlight}
                            >
                              {formatLaneNumber(lane.locationLane)}
                            </FooterLaneChip>
                          </Tooltip>
                        );
                      })}
                    </FooterLaneGrid>
                  </TableFooterExpanded>
                ) : null}
              </TableFooter>
            </TableCard>
          </TablePanel>

          <SidebarPanel $collapsed={isSidebarCollapsed}>
            {isSidebarCollapsed ? (
              <SidebarCollapsedStrip
                type="button"
                aria-label="Expand upcoming loads sidebar"
                onClick={() => setIsSidebarCollapsed(false)}
              >
                <Icon icon={faAnglesLeft} />
                <SidebarCollapsedLabel>Upcoming Loads</SidebarCollapsedLabel>
              </SidebarCollapsedStrip>
            ) : (
              <>
                <SidebarTop>
                  {assigningLane ? (
                    <SidebarAssignBanner>
                      <SidebarAssignBannerTitle>
                        Assigning to Lane {assigningLane} — pick a load
                      </SidebarAssignBannerTitle>
                      <CloseSidebarButton
                        type="button"
                        $light
                        aria-label="Close assign panel"
                        onClick={() => setAssigningLane(undefined)}
                      >
                        ×
                      </CloseSidebarButton>
                    </SidebarAssignBanner>
                  ) : null}

                  <SidebarHeader>
                    <SidebarTitle>Upcoming Loads</SidebarTitle>
                    <SidebarHeaderActions>
                      <PendingBadge>
                        {upcomingLoads.length} pending
                      </PendingBadge>
                      <SidebarCollapseToggle
                        type="button"
                        aria-label="Collapse upcoming loads sidebar"
                        onClick={() => setIsSidebarCollapsed(true)}
                      >
                        <Icon icon={faAnglesRight} />
                      </SidebarCollapseToggle>
                    </SidebarHeaderActions>
                  </SidebarHeader>

                  <UpcomingDayPicker
                    value={upcomingDayFilter}
                    onChange={setUpcomingDayFilter}
                  />
                </SidebarTop>

                <SidebarMiddle>
                  {assignableUpcomingLoads ? (
                    assignableUpcomingLoads.length > 0 ? (
                      <UpcomingLoadList>
                        {assignableUpcomingLoads.map((load) => (
                          <UpcomingLoadCardItem
                            key={load.movementID}
                            load={load}
                            assignableLanes={assignableLaneNumbers}
                            assigningLane={assigningLane}
                            isAssigning={
                              isAssigningLoad &&
                              assigningMovementId === load.movementID
                            }
                            canOperate={canOperate}
                            onAssignClick={(lane) =>
                              handleAssignLoad(
                                lane,
                                load.movementID,
                                load.movementRef,
                              )
                            }
                            onEditEta={(upcomingLoad) =>
                              openDateTimeModal({
                                field: 'etaDate',
                                movementId: upcomingLoad.movementID,
                                movementRef: upcomingLoad.movementRef,
                                value: upcomingLoad.etaDate,
                              })
                            }
                          />
                        ))}
                      </UpcomingLoadList>
                    ) : (
                      <EmptyState>No upcoming loads for this day</EmptyState>
                    )
                  ) : (
                    <EmptyState>
                      The lane is not available for this day
                    </EmptyState>
                  )}
                </SidebarMiddle>

                <SidebarBottom>
                  <SidebarSectionTitle>VACANT LANES</SidebarSectionTitle>
                  {vacantLaneNumbers.length > 0 ? (
                    <LaneGrid>
                      {vacantLaneNumbers.map((laneNumber) => (
                        <LaneChip key={laneNumber} type="button">
                          {laneNumber}
                        </LaneChip>
                      ))}
                    </LaneGrid>
                  ) : (
                    <EmptyState>No vacant lanes</EmptyState>
                  )}
                </SidebarBottom>
              </>
            )}
          </SidebarPanel>
        </MainPanel>
      </PageLayout>

      <Modal
        title={
          dateTimeModal
            ? `${DATE_TIME_MODAL_COPY[dateTimeModal.field].title} — ${dateTimeModal.movementRef}`
            : undefined
        }
        open={Boolean(dateTimeModal)}
        okText="Save"
        confirmLoading={isUpdatingMovement}
        onOk={() => void handleSaveDateTime()}
        onCancel={() => {
          setDateTimeModal(undefined);
          setDateTimeValue(null);
        }}
      >
        <DatePicker
          showTime
          value={dateTimeValue}
          format={FORMAT_DATE_WITH_TIME_DAYJS}
          style={{ width: '100%' }}
          onChange={(value) => setDateTimeValue(value)}
        />
      </Modal>
      <Modal
        title="Job Chain Detail"
        centered
        open={!!selectedJobChainId}
        footer={null}
        destroyOnHidden
        width="90vw"
        styles={{
          body: {
            maxHeight: 'calc(90vh - 60px)',
            overflowY: 'auto',
          },
        }}
        onCancel={() => setSelectedJobChainId(null)}
      >
        {selectedJobChainId !== null && (
          <JobChainDetail
            jobChainId={selectedJobChainId}
            jobStatus={detailJobStatus}
          />
        )}
      </Modal>
    </PageShell>
  );
};

export default LoadLaneStatusPage;
